import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export default function UserBasicsStep({ defaultValues, onSubmit }) {
  const { t, i18n } = useTranslation();

  const languageOptions = useMemo(() => ([
    { value: 'en', label: t('wizard.user_basics.languages.en') },
    { value: 'fr', label: t('wizard.user_basics.languages.fr') }
  ]), [t]);

  const sexOptions = useMemo(() => ([
    { value: 'male', label: t('wizard.user_basics.sex.male') },
    { value: 'female', label: t('wizard.user_basics.sex.female') },
    { value: 'other', label: t('wizard.user_basics.sex.other') }
  ]), [t]);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: {
      preferred_language: defaultValues?.preferred_language || (i18n.language?.startsWith('fr') ? 'fr' : 'en'),
      display_name: defaultValues?.display_name || '',
      age: defaultValues?.age ?? '',
      sex: defaultValues?.sex || '',
      weight_kg: defaultValues?.weight_kg ?? ''
    }
  });

  const preferredLanguage = watch('preferred_language');

  // If defaults change (e.g. after Strava login), update the form *only if user hasn't started typing*.
  // This avoids losing in-progress edits.
  useEffect(() => {
    if (isDirty) return;
    reset({
      preferred_language: defaultValues?.preferred_language || (i18n.language?.startsWith('fr') ? 'fr' : 'en'),
      display_name: defaultValues?.display_name || '',
      age: defaultValues?.age ?? '',
      sex: defaultValues?.sex || '',
      weight_kg: defaultValues?.weight_kg ?? ''
    });
  }, [defaultValues, i18n.language, isDirty, reset]);

  const submit = (data) => {
    // Normalize types (react-hook-form returns strings)
    const payload = {
      preferred_language: data.preferred_language,
      display_name: (data.display_name || '').trim(),
      age: data.age !== '' ? parseInt(data.age, 10) : null,
      sex: data.sex,
      weight_kg: data.weight_kg !== '' ? parseFloat(data.weight_kg) : null
    };
    onSubmit(payload);
  };

  return (
    <div className="max-w-2xl mx-auto p-8 bg-white shadow-2xl shadow-indigo-100/50 rounded-3xl mt-4 md:mt-10 border border-gray-100">
      <h2 className="text-3xl font-black mb-3 text-gray-900 tracking-tight">{t('wizard.user_basics.title')}</h2>
      <p className="text-gray-500 mb-8 font-medium">{t('wizard.user_basics.subtitle')}</p>

      <form onSubmit={handleSubmit(submit)} className="space-y-6">
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
            {t('wizard.user_basics.fields.preferred_language')}
          </label>
          <div className="relative">
            <select
              {...register('preferred_language', { required: true })}
              className="block w-full border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium appearance-none"
            >
              <option value="">{t('wizard.user_basics.placeholders.select')}</option>
              {languageOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
            </div>
          </div>
          {errors.preferred_language && (
            <div className="text-sm text-red-600 mt-2 font-medium">{t('wizard.user_basics.errors.required')}</div>
          )}
          <div className="text-xs text-gray-400 mt-2 font-medium">
            {t('wizard.user_basics.hints.language_applies_immediately', { lang: preferredLanguage?.toUpperCase?.() || '' })}
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
            {t('wizard.user_basics.fields.display_name')}
          </label>
          <input
            type="text"
            {...register('display_name', { required: true, minLength: 2 })}
            className="block w-full border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium placeholder:text-gray-400"
            placeholder={t('wizard.user_basics.placeholders.display_name')}
          />
          {errors.display_name && (
            <div className="text-sm text-red-600 mt-2 font-medium">{t('wizard.user_basics.errors.required')}</div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
              {t('wizard.user_basics.fields.age')}
            </label>
            <input
              type="number"
              inputMode="numeric"
              {...register('age', { required: true, min: 5, max: 120 })}
              className="block w-full border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium placeholder:text-gray-400"
              placeholder={t('wizard.user_basics.placeholders.age')}
            />
            {errors.age && (
              <div className="text-sm text-red-600 mt-2 font-medium">{t('wizard.user_basics.errors.age')}</div>
            )}
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
              {t('wizard.user_basics.fields.sex')}
            </label>
            <div className="relative">
              <select
                {...register('sex', { required: true })}
                className="block w-full border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium appearance-none"
              >
                <option value="">{t('wizard.user_basics.placeholders.select')}</option>
                {sexOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
              </div>
            </div>
            {errors.sex && (
              <div className="text-sm text-red-600 mt-2 font-medium">{t('wizard.user_basics.errors.required')}</div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
            {t('wizard.user_basics.fields.weight_kg')}
          </label>
          <input
            type="number"
            step="0.1"
            inputMode="decimal"
            {...register('weight_kg', { required: true, min: 20, max: 300 })}
            className="block w-full border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium placeholder:text-gray-400"
            placeholder={t('wizard.user_basics.placeholders.weight_kg')}
          />
          {errors.weight_kg && (
            <div className="text-sm text-red-600 mt-2 font-medium">{t('wizard.user_basics.errors.weight')}</div>
          )}
        </div>

        <div className="pt-6 flex justify-end">
          <button
            type="submit"
            className="bg-indigo-600 text-white px-8 py-3.5 rounded-full shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:-translate-y-0.5 hover:bg-indigo-700 transition-all duration-200 font-bold text-sm tracking-wide uppercase"
          >
            {t('wizard.next')}
          </button>
        </div>
      </form>
    </div>
  );
}


